/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { authClientsSetAudience } from "../funcs/authClientsSetAudience.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { AuthClientAudience } from "../models/components/authclientaudience.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SetAuthClientAudienceRequest,
  SetAuthClientAudienceSecurity,
} from "../models/operations/setauthclientaudience.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SetAuthClientAudienceMutationVariables = {
  request: SetAuthClientAudienceRequest;
  security?: SetAuthClientAudienceSecurity | undefined;
  options?: RequestOptions;
};

export type SetAuthClientAudienceMutationData = AuthClientAudience;

export type SetAuthClientAudienceMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * setAuthClientAudience authClients
 *
 * @remarks
 * Replace the rules naming an auth client. Use grants mcp:auth on the client and Blocked writes mcp:blocked_auth; both apply on every server that offers the client. Server access is unchanged and still needs mcp:connect. Administering a client gives nobody permission to use it. Requires org:admin.
 */
export function useSetAuthClientAudienceMutation(
  options?: MutationHookOptions<
    SetAuthClientAudienceMutationData,
    SetAuthClientAudienceMutationError,
    SetAuthClientAudienceMutationVariables
  >,
): UseMutationResult<
  SetAuthClientAudienceMutationData,
  SetAuthClientAudienceMutationError,
  SetAuthClientAudienceMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSetAuthClientAudienceMutation(client, options),
    ...options,
  });
}

export function mutationKeySetAuthClientAudience(): MutationKey {
  return ["@gram/client", "authClients", "setAudience"];
}

export function buildSetAuthClientAudienceMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: SetAuthClientAudienceMutationVariables,
  ) => Promise<SetAuthClientAudienceMutationData>;
} {
  return {
    mutationKey: mutationKeySetAuthClientAudience(),
    mutationFn: function setAuthClientAudienceMutationFn({
      request,
      security,
      options,
    }): Promise<SetAuthClientAudienceMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(authClientsSetAudience(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
